// ─────────────────────────────────────────────────────────────────────────────
// SAP Insights POC — Platform Summary Panel
//
// Trimmed copy of automation-insights/PlatformSummaryPanel.tsx: only the
// Platform Table + assignment-group drill-down (the two things the user
// explicitly asked for), source filter is All/TORI/Human only (no
// "Automation" tab — no automation exists on this pipeline), no
// auto_fulfilled/sap_resolved/overall_resolved/resolution_pct columns, no
// Excel export (not requested for this POC), no AssignmentGroupScopeContext
// (that context links Platform Summary to a sibling Platform Performance
// panel on the automation page; this page has no such sibling panel).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { SAP_PLATFORM_LABEL_BY_KEY } from '../../config/sapPlatforms';
import { buildSapTimeFilterRequestParams, useSapFilters } from '../../contexts/SapFilterContext';
import {
  SapBasis,
  SapPlatformMetrics,
  SapPlatformTableData,
  SapPlatformTableGroupRow,
  SapSourceFilterType,
  SapTicketType,
  fetchSapPlatformTable,
  fetchSapPlatformTableGroups,
  fetchSapTicketDrilldown,
} from '../../services/sapInsightsService';
import TicketDrilldownModal, { DrilldownContext } from '../common/TicketDrilldownModal';

type MetricKey = keyof SapPlatformMetrics;
type MetricCategory = 'volumes' | 'operations' | 'performance';
type DisplayMode = 'count' | 'percentage';
type DisplayModeMetric = 'vol_resolved' | 'cne_count' | 'reopen_count';

interface DisplayModeConfig {
  metric: DisplayModeMetric;
  defaultMode: DisplayMode;
}

interface ColumnConfig {
  id: MetricKey;
  label: string;
  category: MetricCategory;
  displayModeConfig?: DisplayModeConfig;
  // Volume Created/Resolved are the Created-basis cohort counters — hidden
  // in the Resolved basis, same convention as the automation page.
  createdOnly?: boolean;
  // Overall Resolved/SAP Resolution/Resolution % only make sense in the
  // Resolved basis (see sapInsightsService.ts's SapPlatformMetrics doc
  // comment) — hidden whenever selectedBasis !== 'resolved', regardless of
  // saved column-visibility prefs, same convention as createdOnly above.
  resolvedOnly?: boolean;
  // Open Tickets only exists on the Created view (tickets created in the
  // range that are still open) — removed entirely from the table and from
  // Table Settings on the Resolved view, not just hidden by default.
  createdViewOnly?: boolean;
}

const SOURCE_SEGMENTS: Array<{ label: string; value: SapSourceFilterType }> = [
  { label: 'All', value: 'all' },
  { label: 'Tori', value: 'tori' },
  { label: 'Human', value: 'human' },
];

const METRIC_CATEGORY_LABELS: Record<MetricCategory, string> = {
  volumes: 'Volumes',
  operations: 'Operations',
  performance: 'Performance',
};

const COLUMNS: ColumnConfig[] = [
  { id: 'vol_created', label: 'Volume Created', category: 'volumes', createdOnly: true },
  {
    id: 'vol_resolved',
    label: 'Volume Resolved',
    category: 'volumes',
    displayModeConfig: { metric: 'vol_resolved', defaultMode: 'count' },
    createdOnly: true,
  },
  { id: 'open_tickets', label: 'Open Tickets', category: 'volumes', createdViewOnly: true },
  // Resolved View only — see SapPlatformMetrics' doc comment in
  // sapInsightsService.ts for exactly what each one means.
  { id: 'overall_resolved', label: 'Overall Resolved', category: 'volumes', resolvedOnly: true },
  { id: 'sap_resolved', label: 'SAP Resolution', category: 'volumes', resolvedOnly: true },
  { id: 'resolution_pct', label: 'Resolution %', category: 'performance', resolvedOnly: true },
  { id: 'tori_vol', label: 'TORI Volume', category: 'operations' },
  {
    id: 'cne_count',
    label: 'CNE Count',
    category: 'operations',
    displayModeConfig: { metric: 'cne_count', defaultMode: 'count' },
  },
  {
    id: 'reopen_count',
    label: 'Reopen Count',
    category: 'operations',
    displayModeConfig: { metric: 'reopen_count', defaultMode: 'count' },
  },
  { id: 'avg_mttr', label: 'Avg MTTR (hours)', category: 'performance' },
  { id: 'avg_mttr_p50', label: 'P50', category: 'performance' },
  { id: 'avg_mttr_p80', label: 'P80', category: 'performance' },
  { id: 'avg_mttr_p85', label: 'P85', category: 'performance' },
  { id: 'avg_mttr_p90', label: 'P90', category: 'performance' },
];

const CLICKABLE_COLUMNS = new Set<MetricKey>([
  'vol_created', 'vol_resolved', 'tori_vol', 'cne_count',
  'reopen_count', 'open_tickets', 'overall_resolved', 'sap_resolved',
]);

// Default columns per view, in table order (COLUMNS order). Everything else
// is hidden until the user turns it on in Table Settings.
const DEFAULT_COLUMNS_BY_BASIS: Record<SapBasis, MetricKey[]> = {
  created: [
    'vol_created', 'vol_resolved', 'open_tickets', 'tori_vol', 'cne_count',
    'reopen_count', 'avg_mttr', 'avg_mttr_p50', 'avg_mttr_p90',
  ],
  resolved: [
    'overall_resolved', 'sap_resolved', 'tori_vol', 'cne_count',
    'reopen_count', 'avg_mttr', 'avg_mttr_p50', 'avg_mttr_p90',
  ],
};

function getSmartDefaultVisibility(basis: SapBasis, sourceFilter: SapSourceFilterType): Record<MetricKey, boolean> {
  const visible = new Set<MetricKey>(DEFAULT_COLUMNS_BY_BASIS[basis]);
  const visibility = COLUMNS.reduce((acc, c) => {
    acc[c.id] = visible.has(c.id);
    return acc;
  }, {} as Record<MetricKey, boolean>);
  if (sourceFilter === 'human') {
    visibility.tori_vol = false;
  }
  return visibility;
}

const DEFAULT_DISPLAY_MODES: Record<DisplayModeMetric, DisplayMode> = {
  vol_resolved: 'count',
  cne_count: 'count',
  reopen_count: 'count',
};

const STORAGE_KEY = 'sap-insights:column-visibility:v2';
const STORAGE_VERSION = 1;

const TICKET_TYPE_DISPLAY_LABEL: Record<SapTicketType, string> = {
  ALL: 'ALL',
  INC: 'INC',
  RITM: 'SR',
};

function formatValue(value: number | null | undefined, key: MetricKey): string {
  if (value === undefined || value === null) return 'N/A';
  if (key.includes('pct')) return `${value.toFixed(1)}%`;
  if (key.includes('mttr')) return `${value.toFixed(1)}h`;
  return value.toLocaleString();
}

export default function SapPlatformSummaryPanel() {
  const { filters } = useSapFilters();

  const [selectedSourceFilter, setSelectedSourceFilter] = useState<SapSourceFilterType>('all');
  const [selectedTicketType, setSelectedTicketType] = useState<SapTicketType>('ALL');
  const [selectedBasis, setSelectedBasis] = useState<SapBasis>('created');
  const [columnVisibility, setColumnVisibility] = useState<Record<MetricKey, boolean>>(
    getSmartDefaultVisibility('created', 'all'),
  );
  const [displayModes, setDisplayModes] = useState<Record<DisplayModeMetric, DisplayMode>>(DEFAULT_DISPLAY_MODES);
  const [hasCustomizedColumns, setHasCustomizedColumns] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);

  const [data, setData] = useState<SapPlatformTableData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expandedPlatformKey, setExpandedPlatformKey] = useState<string | null>(null);
  const [groupRows, setGroupRows] = useState<SapPlatformTableGroupRow[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const [drilldown, setDrilldown] = useState<DrilldownContext | null>(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== STORAGE_VERSION) return;

      const visibility = COLUMNS.reduce((acc, c) => {
        const v = parsed.visibility?.[c.id];
        acc[c.id] = typeof v === 'boolean' ? v : true;
        return acc;
      }, {} as Record<MetricKey, boolean>);

      const modes = (Object.keys(DEFAULT_DISPLAY_MODES) as DisplayModeMetric[]).reduce((acc, m) => {
        const v = parsed.displayModes?.[m];
        acc[m] = v === 'percentage' || v === 'count' ? v : DEFAULT_DISPLAY_MODES[m];
        return acc;
      }, {} as Record<DisplayModeMetric, DisplayMode>);

      setColumnVisibility(visibility);
      setDisplayModes(modes);
      setHasCustomizedColumns(true);
    } catch {
      // ignore corrupted storage
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, visibility: columnVisibility, displayModes }));
    } catch {
      // ignore storage permission failures
    }
  }, [columnVisibility, displayModes]);

  useEffect(() => {
    if (hasCustomizedColumns) return;
    setColumnVisibility(getSmartDefaultVisibility(selectedBasis, selectedSourceFilter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBasis, selectedSourceFilter, hasCustomizedColumns]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const timeParams = buildSapTimeFilterRequestParams(filters);

    fetchSapPlatformTable({
      ticketType: selectedTicketType,
      sourceFilter: selectedSourceFilter,
      timeFilterType: timeParams.timeFilterType,
      selectedPeriods: timeParams.selectedPeriods,
      dateFrom: timeParams.dateFrom,
      dateTo: timeParams.dateTo,
      basis: selectedBasis,
    })
      .then((response) => {
        if (cancelled) return;
        setData(response);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load SAP platform table');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSourceFilter, selectedTicketType, selectedBasis, filters]);

  useEffect(() => {
    if (!expandedPlatformKey) {
      setGroupRows([]);
      setGroupsError(null);
      return;
    }

    let cancelled = false;
    setGroupsLoading(true);
    setGroupsError(null);

    const timeParams = buildSapTimeFilterRequestParams(filters);

    fetchSapPlatformTableGroups({
      platform: expandedPlatformKey,
      ticketType: selectedTicketType,
      sourceFilter: selectedSourceFilter,
      timeFilterType: timeParams.timeFilterType,
      selectedPeriods: timeParams.selectedPeriods,
      dateFrom: timeParams.dateFrom,
      dateTo: timeParams.dateTo,
      basis: selectedBasis,
    })
      .then((response) => {
        if (cancelled) return;
        setGroupRows(response.rows ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setGroupsError(err?.message || 'Failed to load assignment groups');
        setGroupRows([]);
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [expandedPlatformKey, selectedTicketType, selectedSourceFilter, selectedBasis, filters]);

  const tableRows = useMemo(() => data?.rows ?? [], [data]);
  const showTicketTypeColumn = selectedTicketType === 'ALL';

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => columnVisibility[c.id] && !(c.createdViewOnly && selectedBasis !== 'created')),
    [columnVisibility, selectedBasis],
  );
  const columnGroups = useMemo(() => {
    const grouped = new Map<MetricCategory, ColumnConfig[]>();
    COLUMNS.filter((c) => !(c.createdViewOnly && selectedBasis !== 'created')).forEach((c) => {
      const bucket = grouped.get(c.category);
      if (bucket) bucket.push(c);
      else grouped.set(c.category, [c]);
    });
    return Array.from(grouped.entries()).map(([category, columns]) => ({ category, label: METRIC_CATEGORY_LABELS[category], columns }));
  }, [selectedBasis]);

  const getMetricText = useCallback(
    (columnId: string, value: number | null | undefined, row: SapPlatformMetrics): string => {
      const metricKey = columnId as MetricKey;

      if (
        (metricKey === 'vol_resolved' && displayModes.vol_resolved === 'percentage') ||
        (metricKey === 'cne_count' && displayModes.cne_count === 'percentage') ||
        (metricKey === 'reopen_count' && displayModes.reopen_count === 'percentage')
      ) {
        const denominator = row.vol_created;
        if (!denominator || value === undefined || value === null) return 'N/A';
        const ratio = (value / denominator) * 100;
        return `${ratio.toFixed(1)}%`;
      }

      return formatValue(value, metricKey);
    },
    [displayModes],
  );

  const handleToggleExpand = (platformKey: string) => {
    setExpandedPlatformKey((prev) => (prev === platformKey ? null : platformKey));
  };

  // Every click on Created/Resolved applies that view's default column list
  // (the user can still customise afterwards, until the next click).
  const handleBasisChange = (basis: SapBasis) => {
    setSelectedBasis(basis);
    setHasCustomizedColumns(false);
    setColumnVisibility(getSmartDefaultVisibility(basis, selectedSourceFilter));
  };

  const updateColumnVisibility = (id: MetricKey, checked: boolean) => {
    setHasCustomizedColumns(true);
    setColumnVisibility((prev) => ({ ...prev, [id]: checked }));
  };
  const updateDisplayMode = (metric: DisplayModeMetric, mode: DisplayMode) => setDisplayModes((prev) => ({ ...prev, [metric]: mode }));
  const resetTableSettings = () => {
    setHasCustomizedColumns(false);
    setColumnVisibility(getSmartDefaultVisibility(selectedBasis, selectedSourceFilter));
    setDisplayModes({ ...DEFAULT_DISPLAY_MODES });
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const openDrilldown = (
    platformKey: string,
    platformDisplayName: string,
    colId: MetricKey,
    colLabel: string,
    groupDisplayLabel?: string,
    groupApiParam?: string,
  ) => {
    const timeParams = buildSapTimeFilterRequestParams(filters);
    const agLabel = groupDisplayLabel ? ` · ${groupDisplayLabel}` : '';
    setDrilldown({
      title: `${platformDisplayName} — ${colLabel}${agLabel}`,
      fetchPage: (page) =>
        fetchSapTicketDrilldown({
          platform: platformKey,
          column: colId,
          basis: selectedBasis,
          ticketType: selectedTicketType,
          sourceFilter: selectedSourceFilter,
          timeFilterType: timeParams.timeFilterType,
          selectedPeriods: timeParams.selectedPeriods,
          dateFrom: timeParams.dateFrom,
          dateTo: timeParams.dateTo,
          assignmentGroup: groupApiParam,
          page,
          pageSize: 200,
        }),
    });
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={2} mb={2}>
        <Typography variant="h6" fontWeight={700}>
          Platform Table
        </Typography>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
          <Tooltip
            title={
              selectedBasis === 'created'
                ? 'Created: every number reflects tickets CREATED in the selected date range.'
                : 'Resolved: every number reflects tickets RESOLVED in the selected date range, regardless of when they were created.'
            }
            arrow
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selectedBasis}
              onChange={(_e, v: SapBasis | null) => v && handleBasisChange(v)}
            >
              <ToggleButton value="created">Created</ToggleButton>
              <ToggleButton value="resolved">Resolved</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={selectedSourceFilter}
            onChange={(_e, v: SapSourceFilterType | null) => v && setSelectedSourceFilter(v)}
          >
            {SOURCE_SEGMENTS.map((s) => (
              <ToggleButton key={s.value} value={s.value}>
                {s.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Button size="small" variant="outlined" onClick={(e) => setSettingsAnchorEl(e.currentTarget)}>
            Table Settings
          </Button>
        </Stack>
      </Stack>

      <Popover
        open={Boolean(settingsAnchorEl)}
        anchorEl={settingsAnchorEl}
        onClose={() => setSettingsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 300, maxHeight: 460, overflowY: 'auto', p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              Table Settings
            </Typography>
            <Button size="small" onClick={resetTableSettings}>
              Reset to defaults
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Visible Columns
          </Typography>
          {columnGroups.map((group) => (
            <Box key={group.category} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                {group.label}
              </Typography>
              {group.columns.map((column) => (
                <FormControlLabel
                  key={column.id}
                  sx={{ display: 'flex' }}
                  control={
                    <Checkbox size="small" checked={columnVisibility[column.id]} onChange={(e) => updateColumnVisibility(column.id, e.target.checked)} />
                  }
                  label={<Typography variant="body2">{column.label}</Typography>}
                />
              ))}
            </Box>
          ))}

          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Metric Display
          </Typography>
          {COLUMNS.filter((c) => c.displayModeConfig).map((column) => (
            <Box key={column.id} sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                {column.label}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={displayModes[column.displayModeConfig!.metric]}
                onChange={(_e, v: DisplayMode | null) => v && updateDisplayMode(column.displayModeConfig!.metric, v)}
              >
                <ToggleButton value="count">Count</ToggleButton>
                <ToggleButton value="percentage">Percentage</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ))}
        </Box>
      </Popover>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={selectedTicketType}
        onChange={(_e, v: SapTicketType | null) => v && setSelectedTicketType(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="ALL">ALL</ToggleButton>
        <ToggleButton value="INC">INC</ToggleButton>
        <ToggleButton value="RITM">SR</ToggleButton>
      </ToggleButtonGroup>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <TableContainer sx={{ maxHeight: 560, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', fontWeight: 700 }}>Platform</TableCell>
              {showTicketTypeColumn ? <TableCell sx={{ fontWeight: 700 }}>Ticket Type</TableCell> : null}
              {visibleColumns.map((c) => (
                <TableCell key={c.id} align="center" sx={{ fontWeight: 700 }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                    {showTicketTypeColumn ? (
                      <TableCell>
                        <Skeleton variant="text" width="60%" />
                      </TableCell>
                    ) : null}
                    {visibleColumns.map((c) => (
                      <TableCell key={c.id} align="center">
                        <Skeleton variant="text" width="65%" sx={{ mx: 'auto' }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : !error
              ? tableRows.map((row, index) => {
                  const isExpanded = expandedPlatformKey === row.platform_key;
                  const fullColSpan = 1 + (showTicketTypeColumn ? 1 : 0) + visibleColumns.length;
                  return (
                    <React.Fragment key={`${row.platform_key}-${row.ticket_type}-${index}`}>
                      <TableRow hover selected={isExpanded}>
                        <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Tooltip title={isExpanded ? 'Hide assignment groups' : 'Show assignment groups'}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleExpand(row.platform_key)}
                                aria-label={isExpanded ? 'Collapse assignment groups' : 'Expand assignment groups'}
                              >
                                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <span>{SAP_PLATFORM_LABEL_BY_KEY[row.platform_key] ?? row.platform_name}</span>
                          </Stack>
                        </TableCell>
                        {showTicketTypeColumn ? (
                          <TableCell>{TICKET_TYPE_DISPLAY_LABEL[row.ticket_type] ?? row.ticket_type}</TableCell>
                        ) : null}
                        {visibleColumns.map((c) => (
                          <TableCell key={c.id} align="center">
                            {CLICKABLE_COLUMNS.has(c.id) && row[c.id] != null && row[c.id] !== 0 ? (
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                                onClick={() => openDrilldown(
                                  row.platform_key,
                                  SAP_PLATFORM_LABEL_BY_KEY[row.platform_key] ?? row.platform_name,
                                  c.id, c.label,
                                )}
                              >
                                {getMetricText(c.id, row[c.id], row)}
                              </Typography>
                            ) : (
                              getMetricText(c.id, row[c.id], row)
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={fullColSpan}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1, px: 2, bgcolor: 'action.hover' }}>
                              {groupsLoading ? (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                                  <Skeleton variant="text" width={160} />
                                </Stack>
                              ) : groupsError ? (
                                <Alert severity="warning" sx={{ my: 1 }}>
                                  {groupsError}
                                </Alert>
                              ) : groupRows.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                  No assignment groups found for this platform and filter selection.
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Assignment group</TableCell>
                                      {visibleColumns.map((c) => (
                                        <TableCell key={c.id} align="center" sx={{ fontWeight: 600 }}>
                                          {c.label}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {groupRows.map((groupRow, groupIndex) => (
                                      <TableRow key={`${groupRow.assignment_group}-${groupIndex}`} hover>
                                        <TableCell>{groupRow.assignment_group}</TableCell>
                                        {visibleColumns.map((c) => {
                                          const isOther = groupRow.assignment_group === 'Other teams (escalated out)';
                                          const apiAg = isOther ? '__OTHER__' : groupRow.assignment_group;
                                          return (
                                            <TableCell key={c.id} align="center">
                                              {CLICKABLE_COLUMNS.has(c.id) && groupRow[c.id] != null && groupRow[c.id] !== 0 ? (
                                                <Typography
                                                  component="span"
                                                  variant="body2"
                                                  sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                                                  onClick={() => openDrilldown(
                                                    expandedPlatformKey!,
                                                    SAP_PLATFORM_LABEL_BY_KEY[expandedPlatformKey!] ?? expandedPlatformKey!,
                                                    c.id, c.label,
                                                    groupRow.assignment_group,
                                                    apiAg,
                                                  )}
                                                >
                                                  {getMetricText(c.id, groupRow[c.id], groupRow)}
                                                </Typography>
                                              ) : (
                                                getMetricText(c.id, groupRow[c.id], groupRow)
                                              )}
                                            </TableCell>
                                          );
                                        })}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              : null}

            {!loading && !error && data && tableRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={1 + (showTicketTypeColumn ? 1 : 0) + visibleColumns.length}>
                  <Typography variant="body2" color="text.secondary">
                    No platform rows available for the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading && !error && data?.grand_total ? (
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                  Grand total
                </TableCell>
                {showTicketTypeColumn ? (
                  <TableCell sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>ALL</TableCell>
                ) : null}
                {visibleColumns.map((c) => (
                  <TableCell key={c.id} align="center" sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                    {getMetricText(c.id, data.grand_total?.[c.id], data.grand_total as SapPlatformMetrics)}
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <TicketDrilldownModal context={drilldown} onClose={() => setDrilldown(null)} />
    </Box>
  );
}
